import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Tooltip, IconButton, Collapse, CircularProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import TransactionsService from 'services/transactions.service';
import { ITransaction } from 'common/transactions.interface';
import { useBankingVisibility } from '../../../contexts/BankingVisibilityContext';

interface BilletItem {
  id: number;
  title: string;
  value: string;
}

interface BilletSummary {
  total: string;
  count: number;
  items: BilletItem[];
}

export const SummaryBillets: React.FC = () => {
  const navigate = useNavigate();
  const { showValues } = useBankingVisibility();
  const [expanded, setExpanded] = useState({ overdue: false, today: false, next7: false });
  const [loading, setLoading] = useState(true);

  const [overdueData, setOverdueData] = useState<BilletSummary>({ total: 'R$ 0,00', count: 0, items: [] });
  const [todayData, setTodayData] = useState<BilletSummary>({ total: 'R$ 0,00', count: 0, items: [] });
  const [next7Data, setNext7Data] = useState<BilletSummary>({ total: 'R$ 0,00', count: 0, items: [] });

  const transactionsService = useMemo(() => new TransactionsService(), []);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const parseValue = (value: string | number): number => {
    if (typeof value === 'number') return value;
    // Backend sends value with dot as decimal separator (e.g., "19.000" = 19 reais)
    return parseFloat(value) || 0;
  };

  const transformTransactions = (transactions: ITransaction[] | any): BilletSummary => {
    // Handle case where API returns object with data property instead of array
    const transactionArray = Array.isArray(transactions) ? transactions : transactions?.data || [];

    const items = transactionArray.map((t: ITransaction) => {
      const date = t.bankSlipDetail?.payDueDate
        ? DateTime.fromISO(t.bankSlipDetail.payDueDate).toFormat('dd/MM/yy')
        : DateTime.fromISO(t.createdAt).toFormat('dd/MM/yy');
      const name = t.bankSlipDetail?.payerName || t.counterpartyName || 'N/A';

      return {
        id: t.id,
        title: `${date} | ${name}`,
        value: formatCurrency(parseValue(t.value)),
      };
    });

    const total = transactionArray.reduce((sum: number, t: ITransaction) => sum + parseValue(t.value), 0);

    return {
      total: formatCurrency(total),
      count: transactionArray.length,
      items,
    };
  };

  useEffect(() => {
    let isMounted = true;

    const fetchBilletsSummary = async () => {
      if (!isMounted) return;

      setLoading(true);
      try {
        const today = DateTime.now().startOf('day');
        const tomorrow = today.plus({ days: 1 });
        const next7Days = today.plus({ days: 7 });

        // Fetch overdue billets (payDueDate < today)
        const { items: overdueTransactions } = await transactionsService.getAll({
          payDueDateEnd: today.toISO() || undefined,
          tipo: 'BANK_SLIP_IN',
          accountType: 'CONTROL',
        });

        // Fetch today's billets (payDueDate = today)
        const { items: todayTransactions } = await transactionsService.getAll({
          payDueDateStart: today.toISO() || undefined,
          payDueDateEnd: tomorrow.toISO() || undefined,
          tipo: 'BANK_SLIP_IN',
          accountType: 'CONTROL',
        });

        // Fetch next 7 days billets (payDueDate between tomorrow and next 7 days)
        const { items: next7Transactions } = await transactionsService.getAll({
          payDueDateStart: tomorrow.toISO() || undefined,
          payDueDateEnd: next7Days.toISO() || undefined,
          tipo: 'BANK_SLIP_IN',
          accountType: 'CONTROL',
        });

        // Only update state if component is still mounted
        if (isMounted) {
          setOverdueData(transformTransactions(overdueTransactions));
          setTodayData(transformTransactions(todayTransactions));
          setNext7Data(transformTransactions(next7Transactions));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching billets summary:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchBilletsSummary();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [transactionsService]);

  return (
    <>
      <Box sx={{ mt: '16px' }}>
        <Typography component="span" sx={{ fontSize: '22px', fontWeight: 600 }}>
          Resumo dos meus boletos
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '1vw', alignItems: 'flex-start' }}>
        <Box
          sx={{
            mt: '0px',
            px: '12px',
            py: '8px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #C8004C',
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              Vencidos
              <Tooltip title="Clique aqui e veja todos os boletos" arrow>
                <Box
                  sx={{
                    backgroundColor: '#C8004C',
                    borderRadius: '100%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '12px',
                    ml: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {loading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : overdueData.count}
                </Box>
              </Tooltip>
            </Typography>
            <Typography
              component="span"
              onClick={() => {
                const today = DateTime.now().startOf('day');
                navigate(`/banking/dashboard/receive?payDueDateEnd=${today.toISO()}&kind=boleto`);
              }}
              sx={{
                color: '#115398',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              ver
              <ArrowForwardIcon sx={{ color: '#115398', fontSize: 16, ml: '5px' }} />
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '12px',
              py: '4px',
              borderRadius: '8px',
              backgroundColor: '#F7F6F6',
              mt: '12px',
            }}
          >
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Total
            </Typography>
            <Typography
              component="span"
              sx={{
                color: '#656464',
                fontSize: '22px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {(() => {
                if (loading) return <CircularProgress size={20} sx={{ color: '#656464' }} />;
                return showValues ? overdueData.total : '••••••';
              })()}
              <IconButton
                aria-label="expand overdue"
                size="small"
                onClick={() => setExpanded((prev) => ({ overdue: !prev.overdue, today: false, next7: false }))}
                sx={{ ml: '5px' }}
                disabled={loading}
              >
                <ExpandMoreIcon
                  sx={{ color: '#1C1B1F', transform: expanded.overdue ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                />
              </IconButton>
            </Typography>
          </Box>
          <Collapse in={expanded.overdue} timeout="auto" unmountOnExit>
            <Box sx={{ mt: '8px', borderRadius: '8px' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '12px' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                overdueData.items.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '12px', py: '6px' }}>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500, color: '#434241' }}>
                      {item.title}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, color: '#434241' }}>
                      {showValues ? item.value : '••••••'}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </Box>

        <Box
          sx={{
            mt: '0px',
            px: '12px',
            py: '8px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #CDCCCB',
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              Vence hoje
              <Tooltip title="Clique aqui e veja todos os boletos" arrow>
                <Box
                  sx={{
                    backgroundColor: '#315ED8',
                    borderRadius: '100%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '12px',
                    ml: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {loading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : todayData.count}
                </Box>
              </Tooltip>
            </Typography>
            <Typography
              component="span"
              onClick={() => {
                const today = DateTime.now().startOf('day');
                const tomorrow = today.plus({ days: 1 });
                navigate(`/banking/dashboard/receive?payDueDateStart=${today.toISO()}&payDueDateEnd=${tomorrow.toISO()}&kind=boleto`);
              }}
              sx={{
                color: '#115398',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              ver
              <ArrowForwardIcon sx={{ color: '#115398', fontSize: 16, ml: '5px' }} />
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '12px',
              py: '4px',
              borderRadius: '8px',
              backgroundColor: '#F7F6F6',
              mt: '12px',
            }}
          >
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Total
            </Typography>
            <Typography
              component="span"
              sx={{
                color: '#656464',
                fontSize: '22px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {(() => {
                if (loading) return <CircularProgress size={20} sx={{ color: '#656464' }} />;
                return showValues ? todayData.total : '••••••';
              })()}
              <IconButton
                aria-label="expand today"
                size="small"
                onClick={() => setExpanded((prev) => ({ overdue: false, today: !prev.today, next7: false }))}
                sx={{ ml: '5px' }}
                disabled={loading}
              >
                <ExpandMoreIcon
                  sx={{ color: '#1C1B1F', transform: expanded.today ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                />
              </IconButton>
            </Typography>
          </Box>
          <Collapse in={expanded.today} timeout="auto" unmountOnExit>
            <Box sx={{ mt: '8px', borderRadius: '8px' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '12px' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                todayData.items.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '12px', py: '6px' }}>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500, color: '#434241' }}>
                      {item.title}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, color: '#434241' }}>
                      {showValues ? item.value : '••••••'}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </Box>

        <Box
          sx={{
            mt: '0px',
            px: '12px',
            py: '8px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #CDCCCB',
            flex: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              Vence nos próximos 7 dias
              <Tooltip title="Clique aqui e veja todos os boletos" arrow>
                <Box
                  sx={{
                    backgroundColor: '#315ED8',
                    borderRadius: '100%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '12px',
                    ml: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {loading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : next7Data.count}
                </Box>
              </Tooltip>
            </Typography>
            <Typography
              component="span"
              onClick={() => {
                const today = DateTime.now().startOf('day');
                const tomorrow = today.plus({ days: 1 });
                const next7Days = today.plus({ days: 7 });
                navigate(`/banking/dashboard/receive?payDueDateStart=${tomorrow.toISO()}&payDueDateEnd=${next7Days.toISO()}&kind=boleto`);
              }}
              sx={{
                color: '#115398',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              ver
              <ArrowForwardIcon sx={{ color: '#115398', fontSize: 16, ml: '5px' }} />
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '12px',
              py: '4px',
              borderRadius: '8px',
              backgroundColor: '#F7F6F6',
              mt: '12px',
            }}
          >
            <Typography component="span" sx={{ fontSize: '16px', fontWeight: 600 }}>
              Total
            </Typography>
            <Typography
              component="span"
              sx={{
                color: '#656464',
                fontSize: '22px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {(() => {
                if (loading) return <CircularProgress size={20} sx={{ color: '#656464' }} />;
                return showValues ? next7Data.total : '••••••';
              })()}
              <IconButton
                aria-label="expand next7"
                size="small"
                onClick={() => setExpanded((prev) => ({ overdue: false, today: false, next7: !prev.next7 }))}
                sx={{ ml: '5px' }}
                disabled={loading}
              >
                <ExpandMoreIcon
                  sx={{ color: '#1C1B1F', transform: expanded.next7 ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                />
              </IconButton>
            </Typography>
          </Box>
          <Collapse in={expanded.next7} timeout="auto" unmountOnExit>
            <Box sx={{ mt: '8px', borderRadius: '8px' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '12px' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                next7Data.items.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '12px', py: '6px' }}>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500, color: '#434241' }}>
                      {item.title}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 600, color: '#434241' }}>
                      {showValues ? item.value : '••••••'}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </>
  );
};
